import React, { useEffect, useMemo, useRef, useState } from "react";
import "./BusquedaFacetas.css";

/**
 * Utiles
 */
const API = {
  SEARCH: "/api/meili/search_test.php",
  FACETS: "/api/meili/search_facets.php",
};

const STOPWORDS_ES = new Set([
  "de","la","que","el","en","y","a","los","del","se","las","por","un","para","con","no","una","su","al","lo",
  "como","más","pero","sus","le","ya","o","este","sí","porque","esta","entre","cuando","muy","sin","sobre",
  "también","me","hasta","hay","donde","quien","desde","todo","nos","durante","todos","uno","les","ni","contra",
  "otros","ese","eso","ante","ellos","e","esto","mí","antes","algunos","qué","unos","yo","otro","otras","otra",
  "él","tanto","esa","estos","mucho","quienes","nada","muchos","cual","poco","ella","estar","estas","algunas",
  "algo","nosotros","mi","mis","tú","te","ti","tu","tus","ellas","nosotras","vosotros","vosotras","os","mío",
  "mía","míos","mías","tuyo","tuya","tuyos","tuyas","suyo","suya","suyos","suyas","nuestro","nuestra","nuestros",
  "nuestras","vuestro","vuestra","vuestros","vuestras","esos","esas","estoy","estás","está","estamos","estáis",
  "están","esté","estés","estemos","estéis","estén","estaré","estarás","estará","estaremos","estaréis","estarán",
  "estaba","estabas","estábamos","estabais","estaban","estuve","estuviste","estuvo","estuvimos","estuvisteis",
  "estuvieron","estuviera","estuvieras","estuviéramos","estuvierais","estuvieran","estuviese","estuvieses",
  "estuviésemos","estuvieseis","estuviesen","estando","estado","estada","estados","estadas","estad","un","una","y/o"
]);

const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const tokenize = (text) =>
  normalize(text)
    .split(/[^a-z0-9áéíóúüñ]+/i) // dejamos letras y números, rompemos en el resto
    .filter(Boolean)
    .filter((t) => t.length >= 3 && !STOPWORDS_ES.has(t));

/**
 * Componente principal
 */
export default function BusquedaFacetas() {
  // estado de búsqueda/filtros
  const [q, setQ] = useState("");
  const [localidad, setLocalidad] = useState("");
  const [comercioTipo, setComercioTipo] = useState("");
  const [sort, setSort] = useState(""); // "", "precio_asc", "precio_desc"
  const [limit, setLimit] = useState(20);
  const [categoriasSel, setCategoriasSel] = useState([]); // array de strings (nombres)
  const [etiquetasSel, setEtiquetasSel] = useState([]);



  // resultados
  const [hits, setHits] = useState([]);
  const [facets, setFacets] = useState({
    localidad: {},
    comercio_tipo: {},
    categorias_ids: {},
  });
  const [loading, setLoading] = useState(false);

  // autocompletado
  const [sugsOpen, setSugsOpen] = useState(false);
  const [sugs, setSugs] = useState([]); // {type, label, value, count}
  const [highlight, setHighlight] = useState(-1);
  const sugBoxRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // construir querystring para /search_test.php
  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set("q", q);
    if (localidad) p.set("localidad", localidad);
    if (comercioTipo) p.set("comercio_tipo", comercioTipo);
    if (sort) p.set("sort", sort);
    p.set("limit", String(limit));
    if (categoriasSel.length > 0) {
    p.set("categorias_nombres", categoriasSel.join(","));}
    return p.toString();
      if (etiquetasSel.length > 0) {
    p.set("etiquetas_nombres", etiquetasSel.join(","));  }
  }, [q, localidad, comercioTipo, sort, limit, categoriasSel, etiquetasSel]);

  // Agregar (C): mapa de categorías por NOMBRE a partir de los hits actuales
const catFacetByName = useMemo(() => {
  const m = new Map();
  for (const h of hits) {
    const arr = Array.isArray(h?.categorias_nombres) ? h.categorias_nombres : [];
    for (const raw of arr) {
      const name = String(raw || "").trim();
      if (!name) continue;
      m.set(name, (m.get(name) || 0) + 1);
    }
  }
  // lo devolvemos como objeto para que sea fácil iterar con Object.entries
  return Object.fromEntries([...m.entries()].sort((a,b) => b[1]-a[1]));
}, [hits]);


  // pedir resultados+facetas
  const fetchData = async () => {
    setLoading(true);
    try {
      const [resHits, resFacets] = await Promise.all([
        fetch(`${API.SEARCH}?${params}`).then((r) => r.json()),
        fetch(`${API.FACETS}?q=${encodeURIComponent(q)}&localidad=${encodeURIComponent(localidad)}&comercio_tipo=${encodeURIComponent(comercioTipo)}`).then((r) => r.json()),
      ]);

      setHits(Array.isArray(resHits?.hits) ? resHits.hits : []);
     const fd = resFacets?.facetDistribution || {};
setFacets({
  categorias_ids: fd.categorias_ids || {},
  categorias_nombres: fd.categorias_nombres || {},   // por si lo necesitas
  etiquetas_nombres: fd.etiquetas_nombres || {},     // <-- NUEVO
  comercio_id: fd.comercio_id || {},
  comercio_tipo: fd.comercio_tipo || {},
  localidad: fd.localidad || {},
  precio_min: fd.precio_min || {},
  stock: fd.stock || {},
});

    } catch (e) {
      console.error("❌ Error en fetchData:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  /**
   * AUTOCOMPLETADO
   * - Debounced: 250ms
   * - Fuente: facets (localidad, comercio_tipo) + categorías (de hits) + tokens de texto
   */
  const buildSuggestions = async (text) => {
    // Si no hay texto, sugerimos solo facets más frecuentes
    try {
      // Llamamos a ambos endpoints para construir mejor sugerencia contextual
      const [resHits, resFacets] = await Promise.all([
        fetch(`${API.SEARCH}?q=${encodeURIComponent(text)}&limit=20`).then((r) => r.json()),
        fetch(`${API.FACETS}?q=${encodeURIComponent(text)}`).then((r) => r.json()),
      ]);

      const hitsArr = Array.isArray(resHits?.hits) ? resHits.hits : [];
      const fd = resFacets?.facetDistribution || {};

      // 1) Facetas: LOCALIDAD
      const sugsLoc = Object.entries(fd.localidad || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([label, count]) => ({
          type: "localidad",
          label,
          value: label,
          count,
        }));

      // 2) Facetas: TIPO DE NEGOCIO
      const sugsTipo = Object.entries(fd.comercio_tipo || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([label, count]) => ({
          type: "comercio_tipo",
          label,
          value: label,
          count,
        }));

      // 3) Categorías por nombre (desde hits -> categorias_nombres)
      const catCounter = new Map();
      for (const h of hitsArr) {
        const cats = Array.isArray(h?.categorias_nombres) ? h.categorias_nombres : [];
        for (const c of cats) {
          const key = String(c).trim();
          if (!key) continue;
          catCounter.set(key, (catCounter.get(key) || 0) + 1);
        }
      }
      const sugsCat = [...catCounter.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([label, count]) => ({
          type: "categoria",
          label,
          value: label,
          count,
        }));

      // 4) Tokens de texto (producto_nombre + descripcion)
      const textCounter = new Map();
      const qnorm = normalize(text);
      for (const h of hitsArr) {
        const tokens = new Set([
          ...tokenize(h?.producto_nombre || ""),
          ...tokenize(h?.descripcion || ""),
          ...tokenize(h?.comercio_nombre || ""),
        ]);
        for (const t of tokens) {
          // si hay query, priorizamos tokens que contengan el prefijo de lo escrito
          if (!qnorm || t.includes(qnorm)) {
            textCounter.set(t, (textCounter.get(t) || 0) + 1);
          }
        }
      }
      const sugsText = [...textCounter.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
        .map(([label, count]) => ({
          type: "texto",
          label,
          value: label,
          count,
        }));

      // Ensamblar (limit total ~10–12)
      const merged = [
        ...sugsTipo.slice(0, 3),
        ...sugsLoc.slice(0, 3),
        ...sugsCat.slice(0, 3),
        ...sugsText.slice(0, 6),
      ].slice(0, 12);

      setSugs(merged);
      setSugsOpen(merged.length > 0);
      setHighlight(merged.length ? 0 : -1);
    } catch (e) {
      console.error("❌ Error buildSuggestions:", e);
      setSugsOpen(false);
    }
  };

  const onChangeQ = (e) => {
    const val = e.target.value;
    setQ(val);
    setSugsOpen(!!val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buildSuggestions(val), 250);
  };

  const applySuggestion = (sug) => {
    switch (sug.type) {
      case "localidad":
        setLocalidad(sug.value);
        break;
      case "comercio_tipo":
        setComercioTipo(sug.value);
        break;
      case "categoria":
        // Para ahora: simplemente metemos el texto en q (o podrías implementar un filtro adicional)
        setQ(sug.value);
        break;
      case "texto":
      default:
        setQ(sug.value);
        break;
    }
    setSugsOpen(false);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!sugsOpen || sugs.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % sugs.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + sugs.length) % sugs.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const chosen = sugs[highlight] || sugs[0];
      if (chosen) applySuggestion(chosen);
    } else if (e.key === "Escape") {
      setSugsOpen(false);
    }
  };

  // cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handler = (ev) => {
      if (!sugBoxRef.current) return;
      if (!sugBoxRef.current.contains(ev.target) && ev.target !== inputRef.current) {
        setSugsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // dibujar chips de facetas
  const facetChips = (obj = {}, onPick) =>
    Object.entries(obj)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([key, count]) => (
        <button key={key} className="chip" onClick={() => onPick(key)}>
          {key} <span className="badge">×{count}</span>
        </button>
      ));

  return (
    <div className="bf-wrap">
      <header className="bf-header">
        <h1>Búsqueda con Facetas</h1>
        <div className="bf-actions">
          <button className="btn" onClick={fetchData} disabled={loading}>
            {loading ? "Actualizando…" : "Refrescar"}
          </button>
          <select
            className="select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            title="Ordenar"
          >
            <option value="">Relevancia</option>
            <option value="precio_asc">Precio ↑</option>
            <option value="precio_desc">Precio ↓</option>
          </select>
          <select
            className="select"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            title="Límite"
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} resultados
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="bf-grid">
        <aside className="bf-left">
          <div className="card">
            <div className="card-title">Filtros</div>

            <label className="lbl">Localidad</label>
            <div className="chips">
              {facetChips(facets.localidad, (val) =>
                setLocalidad(val === localidad ? "" : val)
              )}
            </div>

            <label className="lbl">Tipo de negocio</label>
            <div className="chips">
              {facetChips(facets.comercio_tipo, (val) =>
                setComercioTipo(val === comercioTipo ? "" : val)
              )}
            </div>
            <label className="lbl">Categorías</label>
<div className="chips">
  {Object.entries(catFacetByName)
    .slice(0, 20) // muestra las más frecuentes
    .map(([name, count]) => {
      const on = categoriasSel.includes(name);
      return (
        <button
          key={name}
          className={`chip ${on ? "on" : ""}`}
          onClick={() => {
            setCategoriasSel((prev) =>
              on ? prev.filter((x) => x !== name) : [...prev, name]
            );
          }}
          title={name}
        >
          {name} <span className="badge">×{count}</span>
        </button>
      );
    })}
</div>
<label className="lbl">Etiquetas</label>
<div className="chips">
  {Object.entries(facets.etiquetas_nombres || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 20)
    .map(([name, count]) => {
      const on = etiquetasSel.includes(name);
      return (
        <button
          key={name}
          className={`chip ${on ? "on" : ""}`}
          onClick={() => {
            setEtiquetasSel((prev) =>
              on ? prev.filter((x) => x !== name) : [...prev, name]
            );
          }}
          title={name}
        >
          {name} <span className="badge">×{count}</span>
        </button>
      );
    })}
</div>


            <div className="actives">
              {localidad && (
                <span className="active-filter">
                  Localidad: <b>{localidad}</b>{" "}
                  <button className="x" onClick={() => setLocalidad("")} aria-label="Quitar filtro localidad">
                    ×
                  </button>
                </span>
              )}
              {comercioTipo && (
                <span className="active-filter">
                  Tipo: <b>{comercioTipo}</b>{" "}
                  <button className="x" onClick={() => setComercioTipo("")} aria-label="Quitar filtro tipo">
                    ×
                  </button>
                </span>
              )}
              {categoriasSel.length > 0 && (
  <div className="actives">
    {categoriasSel.map((c) => (
      <span key={c} className="active-filter">
        Categoría: <b>{c}</b>{" "}
        <button className="x" onClick={() =>
          setCategoriasSel(categoriasSel.filter((x) => x !== c))
        }>
          ×
        </button>
      </span>
      
    ))}
    {etiquetasSel.length > 0 && (
  <div className="actives">
    {etiquetasSel.map((t) => (
      <span key={t} className="active-filter">
        Etiqueta: <b>{t}</b>{" "}
        <button className="x" onClick={() =>
          setEtiquetasSel(etiquetasSel.filter((x) => x !== t))
        }>
          ×
        </button>
      </span>
    ))}
  </div>
)}

  </div>
)}

            </div>
          </div>
        </aside>

        <main className="bf-main">
          <div className="searchbar">
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={onChangeQ}
              onFocus={() => q && setSugsOpen(true)}
              onKeyDown={onKeyDown}
              placeholder="Buscar por producto, categoría, comercio…"
              aria-label="Buscar"
            />
            {sugsOpen && sugs.length > 0 && (
              <div ref={sugBoxRef} className="sugbox" role="listbox" aria-label="Sugerencias">
                {sugs.map((s, i) => (
                  <div
                    key={`${s.type}:${s.value}:${i}`}
                    role="option"
                    aria-selected={i === highlight}
                    className={`sug ${i === highlight ? "on" : ""}`}
                    onMouseDown={(e) => {
                      e.preventDefault(); // no perder foco antes del click
                      applySuggestion(s);
                    }}
                  >
                    <span className={`tag tag-${s.type}`}>
                      {s.type === "localidad" && "Localidad"}
                      {s.type === "comercio_tipo" && "Tipo"}
                      {s.type === "categoria" && "Categoría"}
                      {s.type === "texto" && "Texto"}
                    </span>
                    <span className="sug-label">{s.label}</span>
                    {typeof s.count === "number" && (
                      <span className="sug-count">×{s.count}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="results-info">
            <span>
              {loading
                ? "Buscando…"
                : `Resultados: ${hits.length} ${
                    localidad ? ` · ${localidad}` : ""
                  }${comercioTipo ? ` · ${comercioTipo}` : ""}`}
            </span>
          </div>

          <div className="cards">
            {hits.map((h) => (
              <article key={h.id} className="card product">
                <div className="prod-top">
                  <h3 className="prod-title">{h.producto_nombre}</h3>
                  <div className="prod-meta">
                    <span className="pill">{h.comercio_nombre}</span>
                    <span className="pill">{h.comercio_tipo}</span>
                    <span className="pill">{h.localidad}</span>
                  </div>
                </div>
                <p className="prod-desc">{h.descripcion}</p>

                {Array.isArray(h.categorias_nombres) && h.categorias_nombres.length > 0 && (
                  <div className="prod-cats">
                    {h.categorias_nombres.map((c, i) => (
                      <span key={i} className="cat">
                        {c}
                      </span>
                    ))}
                  </div>
                )}

                {Array.isArray(h.etiquetas_nombres) && h.etiquetas_nombres.length > 0 && (
  <div className="prod-tags">
    {h.etiquetas_nombres.map((t, i) => (
      <span key={i} className="tag">
        {t}
      </span>
    ))}
  </div>
)}


                <div className="prod-bottom">
                  {typeof h.precio_min === "number" && (
                    <div className="price">
                      <span className="price-label">Desde</span>
                      <span className="price-value">
                        {h.precio_min.toLocaleString("es-ES", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        €
                      </span>
                    </div>
                  )}
                  <div className={`stock ${h.stock ? "ok" : "ko"}`}>
                    {h.stock ? "En stock" : "Sin stock"}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}
